import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CodePreview = ({ file, onCopyCode, onDownloadFile, onSaveFile }) => {
  const [copied, setCopied] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState('');
  const [saving, setSaving] = useState(false);

  const handleCopy = () => {
    const contentToCopy = isEditing ? editedContent : file?.content;
    if (contentToCopy) {
      navigator.clipboard?.writeText(contentToCopy);
      setCopied(true);
      if (onCopyCode) onCopyCode(file);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleEdit = () => {
    setEditedContent(file?.content || '');
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedContent('');
  };

  const handleSave = async () => {
    if (!onSaveFile) return;
    
    setSaving(true);
    try {
      await onSaveFile(file, editedContent);
      setIsEditing(false);
    } catch (error) {
      console.error('Save failed:', error);
    } finally {
      setSaving(false);
    }
  };

  const getLanguageFromExtension = (filename) => {
    if (!filename) return 'text';
    const ext = filename?.split('.')?.pop()?.toLowerCase();
    const languageMap = {
      'js': 'javascript',
      'jsx': 'javascript',
      'ts': 'typescript',
      'tsx': 'typescript',
      'json': 'json',
      'css': 'css',
      'html': 'html',
      'md': 'markdown',
      'py': 'python',
      'sql': 'sql',
      'sh': 'bash'
    };
    return languageMap?.[ext] || 'text';
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${(bytes / Math.pow(k, i))?.toFixed(1)} ${sizes?.[i]}`;
  };

  if (!file) {
    return (
      <div className="h-full flex flex-col items-center justify-center bg-card text-muted-foreground p-8 md:p-12 lg:p-16">
        <Icon name="FileCode2" size={64} className="mb-4 md:mb-6 opacity-50" />
        <h3 className="text-lg md:text-xl lg:text-2xl font-semibold mb-2">No File Selected</h3>
        <p className="text-sm md:text-base text-center max-w-md">
          Select a file from the tree to preview its contents
        </p>
      </div>
    );
  }

  const language = getLanguageFromExtension(file?.name);
  const displayContent = isEditing ? editedContent : file?.content;
  const lineCount = displayContent ? displayContent?.split('\n')?.length : 0;

  return (
    <div className="h-full flex flex-col bg-card">
      <div className="flex-shrink-0 border-b border-border bg-surface-elevated">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 p-3 md:p-4">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="flex-shrink-0 w-10 h-10 md:w-12 md:h-12 rounded-lg bg-primary flex items-center justify-center">
              <Icon name="FileCode2" size={20} color="#FFFFFF" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="font-semibold text-sm md:text-base truncate">{file?.name}</h3>
              <div className="flex items-center gap-3 text-xs md:text-sm text-muted-foreground mt-1">
                <span className="flex items-center gap-1">
                  <Icon name="FileType" size={14} />
                  {language}
                </span>
                <span className="flex items-center gap-1">
                  <Icon name="Hash" size={14} />
                  {lineCount} lines
                </span>
                {file?.size && (
                  <span className="flex items-center gap-1">
                    <Icon name="HardDrive" size={14} />
                    {formatFileSize(file?.size)}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {isEditing ? (
              <>
                <Button
                  variant="default"
                  size="sm"
                  iconName="Save"
                  iconPosition="left"
                  onClick={handleSave}
                  disabled={saving}
                  className="whitespace-nowrap"
                >
                  {saving ? 'Saving...' : 'Save'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="X"
                  iconPosition="left"
                  onClick={handleCancelEdit}
                  disabled={saving}
                  className="whitespace-nowrap"
                >
                  Cancel
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Edit"
                  iconPosition="left"
                  onClick={handleEdit}
                  className="whitespace-nowrap"
                >
                  Edit
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  iconName={copied ? "Check" : "Copy"}
                  iconPosition="left"
                  onClick={handleCopy}
                  className="whitespace-nowrap"
                >
                  {copied ? 'Copied!' : 'Copy'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Download"
                  iconPosition="left"
                  onClick={() => onDownloadFile && onDownloadFile(file)}
                  className="whitespace-nowrap"
                >
                  Download
                </Button>
              </>
            )}
          </div>
        </div>
        <div className="px-3 md:px-4 pb-3 md:pb-4">
          <div className="text-xs md:text-sm text-muted-foreground font-mono bg-muted px-3 py-2 rounded-md truncate">
            {file?.path}
          </div>
        </div>
      </div>
      <div className="flex-1 overflow-auto scrollbar-thin">
        {isEditing ? (
          <div className="h-full">
            <textarea
              value={editedContent}
              onChange={(e) => setEditedContent(e?.target?.value)}
              className="w-full h-full p-4 md:p-6 text-xs md:text-sm leading-6 md:leading-7 font-mono bg-card text-foreground border-none outline-none resize-none"
              spellCheck={false}
            />
          </div>
        ) : (
          <div className="relative">
            <div className="absolute left-0 top-0 bottom-0 w-12 md:w-16 bg-surface-elevated border-r border-border flex flex-col items-end pr-2 md:pr-3 pt-4 md:pt-6 text-xs md:text-sm text-muted-foreground font-mono select-none">
              {Array.from({ length: lineCount }, (_, i) => (
                <div key={i + 1} className="leading-6 md:leading-7">
                  {i + 1}
                </div>
              ))}
            </div>
            <pre className="pl-14 md:pl-20 pr-4 md:pr-6 py-4 md:py-6 text-xs md:text-sm leading-6 md:leading-7 font-mono">
              <code className="text-foreground">{displayContent}</code>
            </pre>
          </div>
        )}
      </div>
    </div>
  );
};

export default CodePreview;